
import { Policy, PolicyReview, PolicyVersion, Acknowledgment } from "@/types";
import { Dispatch, SetStateAction } from "react";

/**
 * Operations related to policy review
 */
export const usePolicyReview = (
  policies: Policy[],
  setPolicies: Dispatch<SetStateAction<Policy[]>>,
  acknowledgments: Acknowledgment[],
  setAcknowledgments: Dispatch<SetStateAction<Acknowledgment[]>>,
  saveToStorage: (updatedPolicies: Policy[], updatedAcknowledgments?: Acknowledgment[]) => void
) => {
  const reviewPolicy = (policyId: string, edited: boolean, reviewerName?: string, nextReviewDate?: Date | null) => {
    const now = new Date().toISOString();
    const updatedPolicies = policies.map(policy => {
      if (policy.id === policyId) {
        // Don't review draft policies
        if (policy.isDraft) return policy;
        
        // Create the base updated policy object
        const updatedPolicy: Policy = {
          ...policy,
          updatedAt: now,
        };
        
        // Only add review date if provided
        if (nextReviewDate) {
          updatedPolicy.reviewDate = nextReviewDate.toISOString();
          updatedPolicy.lastReviewed = now;
          updatedPolicy.reviewedBy = reviewerName || "Unknown";
          
          // Create new review entry
          const newReview: PolicyReview = {
            reviewedBy: reviewerName || "Unknown",
            timestamp: now
          };
          
          updatedPolicy.reviews = [...(policy.reviews || []), newReview];
        }
        
        // Update version if edited
        if (edited) {
          // Create a new version entry for the previous state
          const previousVersion: PolicyVersion = {
            version: policy.version,
            content: policy.content,
            updatedAt: policy.updatedAt
          };
          
          // Increment the version
          const newVersion = (parseFloat(policy.version) + 0.1).toFixed(1);
          updatedPolicy.version = newVersion;
          
          // Add the previous version to history
          updatedPolicy.previousVersions = [
            ...(policy.previousVersions || []),
            previousVersion
          ];
        }
        
        return updatedPolicy;
      }
      return policy;
    });
    
    setPolicies(updatedPolicies);
    saveToStorage(updatedPolicies);
    
    if (edited) {
      // If policy was edited, all previous acknowledgments are invalidated
      const filteredAcknowledgments = acknowledgments.filter(ack => ack.policyId !== policyId);
      setAcknowledgments(filteredAcknowledgments);
      saveToStorage(updatedPolicies, filteredAcknowledgments);
    }
  };

  const getPolicyVersionHistory = (policyId: string) => {
    const policy = policies.find(p => p.id === policyId);
    if (!policy) return [];
    
    // Combine previous versions with current version
    return [
      ...(policy.previousVersions || []),
      {
        version: policy.version,
        content: policy.content,
        updatedAt: policy.updatedAt
      }
    ];
  };

  return {
    reviewPolicy,
    getPolicyVersionHistory
  };
};
